import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Layout from '../ui/Layout';
import Card from '../ui/Card';
import Button from '../ui/Button';
import InputField from '../ui/InputField';
import { Recordatorio } from '../../domain/entities/Recordatorio';
import { useValidacionDiaHabil } from '../hooks/useValidacionDiaHabil';
import ModalDiaNoHabil from '../ui/ModalDiaNoHabil';
import './InvoiceDetailPage.css';
import { formatDate } from '../utils/dateUtils';

const RecordatorioDetailPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [paymentAmount, setPaymentAmount] = useState<number>(0);
  const [isPartialPayment, setIsPartialPayment] = useState<boolean>(false);
  const [showPartialPaymentMessage, setShowPartialPaymentMessage] = useState<boolean>(false);
  const [recordatorio, setRecordatorio] = useState<Recordatorio | null>(null);
  const { validando, mensaje, mostrarModal, validarDiaHabil, aceptarAviso } = useValidacionDiaHabil();

  useEffect(() => {
    if (location.state && 'recordatorio' in location.state) {
      const data = (location.state as { recordatorio: Recordatorio }).recordatorio;
      if (data) {
        setRecordatorio(data);
        const totalAPagar = data.saldoTotalAdeudado + data.valorTotalMora;
        setPaymentAmount(totalAPagar);
      }
    }
  }, [location.state]);

  if (!recordatorio) {
    return (
      <Layout>
        <Card title="Error">
          <div className="error-container">
            <p>No se pudo cargar la información del recordatorio. Por favor, intente nuevamente.</p>
            <Button onClick={() => navigate('/')}>Volver al inicio</Button>
          </div>
        </Card>
      </Layout>
    );
  }

  const totalAPagar = recordatorio.saldoTotalAdeudado + recordatorio.valorTotalMora;

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const amount = parseFloat(e.target.value);
    if (isNaN(amount) || amount <= 0) {
      setPaymentAmount(0);
      setIsPartialPayment(false);
      setShowPartialPaymentMessage(false);
      return;
    }
    setPaymentAmount(amount);
    const isPartial = amount !== totalAPagar;
    setIsPartialPayment(isPartial);
    setShowPartialPaymentMessage(isPartial);
  };

  const irAConfirmacion = () => {
    navigate('/payment-confirmation', {
      state: { recordatorio, paymentAmount, isPartialPayment, tipo: 'RECORDATORIO' }
    });
  };

  const handleContinue = async () => {
    if (await validarDiaHabil()) {
      irAConfirmacion();
    }
  };

  const formatCurrency = (value: number): string => {
    if (value === undefined || value === null || isNaN(value)) return '$0';
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0
    }).format(value);
  };

  return (
    <Layout>
      <Card title="Detalle de Recordatorio de pago">
        <div className="invoice-detail">
          <div className="invoice-info">
            <h3>Detalles</h3>

            <div className="info-row">
              <span className="label">Número de Recordatorio de pago:</span>
              <span className="value">RDP{recordatorio.nroRecordatorio}</span>
            </div>

            <div className="info-row">
              <span className="label">Cliente:</span>
              <span className="value">{recordatorio.nombreCliente || 'N/A'}</span>
            </div>

            <div className="info-row">
              <span className="label">NIT/CC:</span>
              <span className="value">{recordatorio.nitCliente || 'N/A'}</span>
            </div>

            <div className="info-row">
              <span className="label">Fecha Emisión:</span>
              <span className="value">{formatDate(recordatorio.fechaRegistro)}</span>
            </div>


            <div className="info-row">
              <span className="label">Detalle:</span>
              <span className="value detalle-line">
                {(recordatorio.detalle || 'Sin detalle').split('|').map((line, index, arr) => (
                  <React.Fragment key={index}>
                    {line.trim()}
                    {index < arr.length - 1 && <br />}
                  </React.Fragment>
                ))}
              </span>
            </div>

            <div className="info-row">
              <span className="label">Valor saldo del Recordatorio de pago:</span>
              <span className="value highlight">{formatCurrency(recordatorio.saldoTotalAdeudado)}</span>
            </div>

            <div className="info-row">
              <span className="label">Intereses:</span>
              <span className="value highlight">{formatCurrency(recordatorio.valorTotalMora)}</span>
            </div>

            <div className="info-row">
              <span className="label">Monto total a pagar:</span>
              <span className="value highlight">{formatCurrency(totalAPagar)}</span>
            </div>
          </div>

          <div className="payment-section">
            <h3>Información de Pago</h3>

            <InputField
              id="paymentAmount"
              label="Valor a Pagar"
              type="number"
              value={String(paymentAmount || 0)}
              onChange={handleAmountChange}
              min={1}
              max={totalAPagar}
            />

            {showPartialPaymentMessage && (
              <div className="partial-payment-message">
                <p>El pago parcial será aplicado primero a intereses de mora y el restante a capital</p>
              </div>
            )}

            <Button
              onClick={handleContinue}
              disabled={!paymentAmount || paymentAmount <= 0 || validando}
              fullWidth
            >
              {validando ? 'Validando...' : 'Continuar Pago'}
            </Button>
          </div>
        </div>
      </Card>

      <ModalDiaNoHabil
        visible={mostrarModal}
        mensaje={mensaje}
        onAceptar={() => {
          aceptarAviso();
          irAConfirmacion();
        }}
      />
    </Layout>
  );
};

export default RecordatorioDetailPage;
